/**
 * PermissionsHelpTab - Plain-English explanation of the whole permission system.
 *
 * Written for humans, not programmers. Goal: any superadmin can read this once
 * and understand how access works, where the rules live, what is weak today,
 * and how the new access system addresses each point.
 */

import React from 'react';

const C = {
  ink: '#1a1a2e',
  muted: '#6c757d',
  blue: '#3498db',
  green: '#2ecc71',
  orange: '#e67e22',
  red: '#e74c3c',
  purple: '#9b59b6',
  amberBg: '#fff8e6',
  amberBorder: '#f0c36d',
};

const sectionTitle = (icon, text, color = C.ink) => (
  <h2 style={{
    fontSize: 20, color, margin: '48px 0 6px', display: 'flex',
    alignItems: 'center', gap: 10, scrollMarginTop: 80,
  }}>
    <span style={{ fontSize: 24 }}>{icon}</span> {text}
  </h2>
);

const Card = ({ color, title, subtitle, children, style }) => (
  <div style={{
    border: `1px solid ${color}40`, borderTop: `4px solid ${color}`,
    borderRadius: 12, padding: '18px 20px', backgroundColor: '#fff',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)', ...style,
  }}>
    <div style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>{title}</div>
    {subtitle && (
      <div style={{ fontSize: 12, fontWeight: 600, color, margin: '2px 0 8px' }}>{subtitle}</div>
    )}
    <div style={{ fontSize: 13.5, color: '#444', lineHeight: 1.65 }}>{children}</div>
  </div>
);

const Arrow = () => (
  <div style={{
    alignSelf: 'center', color: '#adb5bd', fontSize: 22, fontWeight: 700,
    padding: '0 2px', flexShrink: 0,
  }}>→</div>
);

const Pill = ({ color, children }) => (
  <span style={{
    display: 'inline-block', padding: '2px 10px', borderRadius: 12,
    backgroundColor: `${color}15`, color, border: `1px solid ${color}40`,
    fontSize: 12, fontWeight: 600, margin: '2px 4px 2px 0', whiteSpace: 'nowrap',
  }}>{children}</span>
);

const Code = ({ children }) => (
  <code style={{
    backgroundColor: '#f1f3f5', padding: '1px 6px', borderRadius: 4,
    fontSize: 12, color: '#c0392b',
  }}>{children}</code>
);

const PermissionsHelpTab = () => {
  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '8px 8px 64px', color: '#333' }}>

      {/* ================= HERO ================= */}
      <div style={{
        background: `linear-gradient(135deg, ${C.ink} 0%, #2c2c54 100%)`,
        borderRadius: 16, padding: '36px 40px', color: '#fff', marginBottom: 8,
      }}>
        <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: '#f39c12' }}>
          Permissions, explained
        </div>
        <h1 style={{ margin: '10px 0 12px', fontSize: 28, lineHeight: 1.25 }}>
          Who can see what, and who can do what — in plain English
        </h1>
        <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: '#d5d8e0', maxWidth: 820 }}>
          This page explains the whole access system without programmer language.
          Read it top to bottom once and you will understand every other tab in this section —
          and be able to walk anyone else through it.
        </p>
        <a
          href="/docs/permissions-guide.pdf"
          download="LGL Portal - Permissions Guide.pdf"
          style={{
            display: 'inline-block', marginTop: 18, padding: '9px 18px', borderRadius: 8,
            backgroundColor: '#f39c12', color: '#1a1a2e', fontSize: 13.5, fontWeight: 700,
            textDecoration: 'none',
          }}
        >
          ⬇ Download this guide as PDF (for sharing)
        </a>
      </div>

      {/* ================= THE NEW SYSTEM ================= */}
      {sectionTitle('🧭', 'The new access system, in four ideas', '#1e7e34')}
      <p style={{ fontSize: 14, lineHeight: 1.7, maxWidth: 880 }}>
        The tabs next to this help (<strong>Templates, Assignments, Exceptions, Explainer</strong>) are
        the new access system. Everything in it is stored in the database, editable on these screens,
        and enforced by a single rule-keeper on the server. It rests on four ideas:
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 14, margin: '16px 0' }}>
        <Card color={C.blue} title="Modules × 4 levels" subtitle="One vocabulary for everything">
          Every part of the portal (Progress Tracking, Schedule, Forecasting…) gets exactly one of four levels
          per person: <Pill color="#95a5a6">None</Pill> <Pill color={C.blue}>View</Pill>{' '}
          <Pill color={C.green}>Standard</Pill> <Pill color={C.red}>Admin</Pill>.
          None hides it, View lets you look, Standard lets you work in it, Admin lets you configure it.
          Menus appear automatically at View or better. On top of a level, a person can be handed a single
          extra ability — say, deleting rows, or importing from Excel — without giving them the whole next level.
        </Card>
        <Card color={C.green} title="Templates" subtitle="One choice instead of twenty switches">
          Nobody sets those levels person by person. Instead, the level choices are saved once under a name —
          &ldquo;Project Manager&rdquo;, &ldquo;Estimator&rdquo; — and a new person simply receives that named set.
          Change the set once, and everyone who carries it is updated at the same moment.
          The starting sets were built from today&rsquo;s roles, so nobody&rsquo;s access changed when the
          new system arrived.
        </Card>
        <Card color={C.orange} title="Hats (per-project assignment)" subtitle="The matrix organization, solved">
          Everyone has a company default template, and can carry a <em>different</em> template per project:
          TC on job A, Estimator on job B, PM on job C. The project hat always wins inside that project.
          (External users are the exception: they get project hats only &mdash; see below.)
        </Card>
        <Card color={C.purple} title="Exceptions + Explainer" subtitle="Enforced, audited, explainable">
          One-person exceptions (grant or deny, optionally per project, optionally expiring) that the server
          actually enforces — deny always beats grant. And the Explainer answers &ldquo;why can Sam edit the
          schedule on job X?&rdquo; with the exact reasoning steps.
        </Card>
      </div>
      <div style={{
        backgroundColor: C.amberBg, border: `1px solid ${C.amberBorder}`, borderRadius: 10,
        padding: '12px 16px', fontSize: 13.5, lineHeight: 1.65, maxWidth: 900,
      }}>
        <strong>Cutover status:</strong> Exceptions are enforced <strong>everywhere, today</strong> —
        a portal-wide denial or grant takes effect on every request the moment it is saved.
        Template level changes take over area by area (Progress Tracking first, then the rest);
        until then the previous system&rsquo;s screens stay available in the gray strip above.
      </div>

      {/* ================= THE BADGE ================= */}
      {sectionTitle('🪪', 'The big idea: everyone carries a badge')}
      <p style={{ fontSize: 14, lineHeight: 1.7, maxWidth: 860 }}>
        Think of the portal as an office building. When someone signs in, the portal hands them
        a badge. Everything about access is written on that badge. There are five things on it:
      </p>

      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'stretch', margin: '20px 0' }}>
        {/* Visual badge */}
        <div style={{
          width: 270, flexShrink: 0, borderRadius: 14, border: '1px solid #dee2e6',
          background: '#fff', boxShadow: '0 8px 24px rgba(0,0,0,0.10)', overflow: 'hidden',
          alignSelf: 'flex-start',
        }}>
          <div style={{ backgroundColor: C.ink, color: '#fff', padding: '10px 16px', fontSize: 12, fontWeight: 700, letterSpacing: 1 }}>
            LGL PORTAL &mdash; ACCESS BADGE
          </div>
          <div style={{ padding: 16 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
              <div style={{
                width: 48, height: 48, borderRadius: '50%', backgroundColor: '#f1f3f5',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24,
              }}>👷</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>Sam Example</div>
                <div style={{ fontSize: 11, color: C.muted }}>sam@laframboise.net</div>
              </div>
            </div>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <tbody>
                <tr><td style={{ padding: '4px 0', color: C.muted }}>Job title</td><td style={{ fontWeight: 600 }}>Project Manager</td></tr>
                <tr><td style={{ padding: '4px 0', color: C.muted }}>Rank</td><td><Pill color={C.orange}>Manager</Pill></td></tr>
                <tr><td style={{ padding: '4px 0', color: C.muted }}>Department</td><td><Pill color={C.blue}>Construction</Pill></td></tr>
                <tr><td style={{ padding: '4px 0', color: C.muted }}>Trades</td><td><Pill color={C.green}>Piping</Pill><Pill color={C.green}>Mechanical</Pill></td></tr>
                <tr><td style={{ padding: '4px 0', color: C.muted }}>Projects</td><td style={{ fontWeight: 600 }}>Assigned only</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* The five things */}
        <div style={{ flex: 1, minWidth: 340, display: 'grid', gap: 12 }}>
          <Card color={C.blue} title="1. Job title (we call it a “Role”)" subtitle="Decides WHICH MENUS you see">
            Examples: Project Manager, Technical Coordinator, Cost Clerk. The title places you in a
            department and decides which sections show up in your sidebar, plus whether you can open
            every project or only projects you are assigned to.
          </Card>
          <Card color={C.orange} title="2. Rank (we call it an “Authority Level”)" subtitle="Decides WHAT ACTIONS you can take">
            Separate from the title. Five ranks: System &gt; Upper Management &gt; Manager &gt; Lead &gt; Staff.
            Staff can look, Leads can edit and import/export, Managers can also configure,
            Upper Management can also delete. A person can be given a higher or lower rank
            than their title normally carries, without changing the title.
          </Card>
          <Card color={C.green} title="3. Trades (we call them “Sub-Departments”)" subtitle="Decides WHICH DISCIPLINES are yours">
            Piping, Mechanical, Electrical, Structural&hellip; A Piping person sees the Piping tracker
            and not the Mechanical one. Some job titles automatically get all trades.
          </Card>
          <Card color={C.purple} title="4. Hall passes (we call them “Exceptions”)" subtitle="Exceptions for one person">
            &ldquo;Give Bob delete rights on job 25-104 until Friday&rdquo; or &ldquo;Never let Alice export.&rdquo;
            Stored per person, optionally per project, optionally with an expiry date &mdash; and
            <strong> checked at the door on every request</strong>: a denial always beats a grant.
            (They replace the old &ldquo;overrides&rdquo;, which were recorded but never checked.)
          </Card>
          <Card color={C.red} title="5. Master key (Superadmin)" subtitle="Skips every check">
            A superadmin bypasses all of the above, everywhere. Two accounts are protected in a way
            that they can never lose this key (currently written into the program itself, not the database).
          </Card>
        </div>
      </div>

      {/* ================= THREE QUESTIONS ================= */}
      {sectionTitle('🚪', 'Every click answers three questions')}
      <p style={{ fontSize: 14, lineHeight: 1.7, maxWidth: 860 }}>
        Whenever someone opens a page or presses a button, the portal quietly asks three questions.
        All three must be &ldquo;yes&rdquo;:
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14, margin: '16px 0' }}>
        <Card color={C.blue} title="“Are you in the right department?”" subtitle="The building floor">
          Progress Tracking belongs to Construction. Time Tracker admin belongs to Accounting.
          If your badge says Sales, the Construction floor simply is not on your elevator panel.
        </Card>
        <Card color={C.green} title="“Is this your trade?”" subtitle="The room on that floor">
          Inside Construction, the Piping room is for people with the Piping sticker on their badge.
          No sticker, no room &mdash; even if you are on the right floor.
        </Card>
        <Card color={C.orange} title="“Does your rank allow this action?”" subtitle="What you may touch inside">
          Looking is Staff level. Changing numbers is Lead level. Changing the rules of the room
          (columns, rules of credit) is Manager level. Throwing things away is Upper Management level.
        </Card>
      </div>

      {/* ================= HEADER CONTROLS ================= */}
      {sectionTitle('🧢', 'Departments, projects, estimates — what the header controls really are')}
      <p style={{ fontSize: 14, lineHeight: 1.7, maxWidth: 880 }}>
        A very common question, so let&rsquo;s clear it up. The pickers at the top of the portal are
        <strong> not permissions</strong> — they are navigation. Permissions decide what is inside them:
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14, margin: '16px 0' }}>
        <Card color={C.blue} title="The Department picker" subtitle="A folder switch, not a lock">
          Project Management, Estimation, Accounting&hellip; are <em>folders of menu items</em>.
          Everyone sees every folder name in the list — switching folders only changes which group of
          menus the sidebar shows. The filtering happens <em>inside</em>: your badge decides which items
          appear in each folder. If a folder looks empty to you, that just means your badge grants
          nothing in it.
        </Card>
        <Card color={C.green} title="Select Project / Select Estimate" subtitle="Context pickers">
          These choose <em>which job or estimate you are working in</em> — like opening a file.
          Whether a given job appears in your list is decided by your project reach: some badges reach
          every project, others only the projects (or estimates) they are assigned to. With the new
          system this is also where project hats kick in: pick job A and you may carry a different
          template than on job B.
        </Card>
        <Card color={C.orange} title="The Admin panel" subtitle="Why some people see only two tabs">
          The full admin panel (Users, Permissions, Settings&hellip;) is superadmin-only. Everyone else
          who opens it sees exactly two tabs — <strong>Estimates</strong> and <strong>Projects</strong> —
          because those are working screens (managing job records), not system administration.
          So a manager seeing &ldquo;only projects and estimates&rdquo; there is the system working as designed.
        </Card>
      </div>

      {/* ================= LOGIN FLOW ================= */}
      {sectionTitle('🔄', 'What actually happens when someone signs in')}
      <div style={{
        display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'stretch',
        backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 12, padding: 18, margin: '14px 0',
      }}>
        {[
          { icon: '🔐', title: 'Microsoft sign-in', text: 'The person proves who they are with their company Microsoft account. The portal never stores passwords.' },
          { icon: '🪪', title: 'Badge lookup', text: 'The portal reads their job title, rank, trades and projects from the database and builds the badge.' },
          { icon: '🙈', title: 'Menus get trimmed', text: 'The sidebar hides everything the badge does not allow. This is politeness, not security — just decluttering.' },
          { icon: '🛡️', title: 'The server re-checks everything', text: 'Every single request is checked again on the server. Even if someone forced a hidden button to appear, the server would refuse. This is the real lock.' },
        ].map((s, i, arr) => (
          <React.Fragment key={s.title}>
            <div style={{ flex: 1, minWidth: 190, backgroundColor: '#fff', borderRadius: 10, border: '1px solid #e9ecef', padding: '14px 16px' }}>
              <div style={{ fontSize: 26 }}>{s.icon}</div>
              <div style={{ fontWeight: 700, fontSize: 13.5, margin: '6px 0 4px', color: C.ink }}>{i + 1}. {s.title}</div>
              <div style={{ fontSize: 12.5, color: '#555', lineHeight: 1.6 }}>{s.text}</div>
            </div>
            {i < arr.length - 1 && <Arrow />}
          </React.Fragment>
        ))}
      </div>
      <p style={{ fontSize: 13.5, color: C.muted, maxWidth: 860 }}>
        Rule of thumb: <strong>hiding a menu is a courtesy; the server saying &ldquo;no&rdquo; is the security.</strong>{' '}
        If those two ever disagree, the server wins &mdash; and users see confusing errors. Keeping them in
        sync is one of the main goals of the cleanup.
      </p>

      {/* ================= WHERE RULES LIVE ================= */}
      {sectionTitle('🗄️', 'Where the rules are stored')}
      <p style={{ fontSize: 14, lineHeight: 1.7, maxWidth: 860 }}>
        Almost everything lives in the database (good &mdash; editable from this panel without a programmer).
        A few things are unfortunately written into the program itself (bad &mdash; needs a code change and a
        deployment). Here is the full inventory, in human terms:
      </p>
      <div style={{ overflowX: 'auto', margin: '14px 0' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
          <thead>
            <tr style={{ backgroundColor: C.ink, color: '#fff' }}>
              <th style={{ padding: '10px 14px', textAlign: 'left' }}>What it really is</th>
              <th style={{ padding: '10px 14px', textAlign: 'left' }}>Database name</th>
              <th style={{ padding: '10px 14px', textAlign: 'left' }}>Edited from</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['The list of departments (the building floors)', 'auth.departments', 'Roles tab'],
              ['The list of trades inside each department (the rooms)', 'auth.sub_departments', 'Roles tab'],
              ['The job titles: department, default rank, menus, project reach', 'auth.role_templates', 'Roles tab'],
              ['The five ranks and which actions each rank may take', 'auth.authority_levels', 'Authority Levels tab'],
              ['Each person: their title, their personal rank override', 'auth.users', 'Users tab'],
              ['Second job titles for people wearing two hats', 'auth.user_secondary_roles', 'Users tab'],
              ['Which trades each person is assigned to', 'auth.user_sub_departments', 'Users tab'],
              ['Which projects each person is assigned to (with room for a per-project title)', 'auth.user_project_assignments', 'Users tab / Projects tab'],
              ['The hall passes: one-off grants and denials', 'auth.permission_overrides', 'Overrides tab'],
              ['The diary: who changed whose access, when', 'auth.permission_audit_log', 'read-only'],
            ].map(([what, table, where], idx) => (
              <tr key={table} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                <td style={{ padding: '8px 14px', borderBottom: '1px solid #f0f0f0' }}>{what}</td>
                <td style={{ padding: '8px 14px', borderBottom: '1px solid #f0f0f0' }}><Code>{table}</Code></td>
                <td style={{ padding: '8px 14px', borderBottom: '1px solid #f0f0f0', color: C.muted }}>{where}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{
        backgroundColor: C.amberBg, border: `1px solid ${C.amberBorder}`, borderRadius: 10,
        padding: '12px 16px', fontSize: 13.5, lineHeight: 1.65, maxWidth: 900,
      }}>
        <strong>Still written into the program (not the database):</strong> the department-to-menu map,
        the discipline-to-tracker map, several &ldquo;who counts as a foreman / as accounting&rdquo; lists,
        the two protected superadmin emails, and a handful of one-off menu exceptions. Changing any of
        these today requires a programmer and a deployment. Moving them into the database is a core part
        of the planned cleanup.
      </div>

      {/* ================= THE TABS ================= */}
      {sectionTitle('🧭', 'What each tab in this section does')}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14, margin: '16px 0' }}>
        <Card color={C.blue} title="Overview" subtitle="Read-only map">
          The bird&rsquo;s-eye view: every job title against every menu section, every rank against every
          action, plus the department and trade maps. Look here first when someone says &ldquo;I can&rsquo;t
          see X&rdquo;. The same read-only view also exists under the PMO menu for reference.
        </Card>
        <Card color={C.green} title="Role Matrix" subtitle="Read-only grid">
          Job titles against individual permissions in one grid, with filters and CSV export.
          Click a title to see who currently holds it.
        </Card>
        <Card color={C.orange} title="Roles" subtitle="Editor">
          Create and edit job titles: their department, default rank, which menus they see,
          and how far their project reach goes.
        </Card>
        <Card color={C.red} title="Authority Levels" subtitle="Editor">
          Edit what each of the five ranks is allowed to do. Changing a rank here instantly
          changes every person holding that rank.
        </Card>
        <Card color={C.purple} title="Overrides (legacy)" subtitle="Replaced — kept for reference">
          The old hall passes. They were never checked at the door, so they have been replaced by
          the <strong>Exceptions</strong> tab, which is enforced on every request. This screen stays
          only until the old system is removed.
        </Card>
        <Card color={C.ink} title="Tester" subtitle="Simulator">
          Pick any person and any permission and ask &ldquo;would this be allowed, and why?&rdquo;
          Use it before and after making changes to confirm the effect.
        </Card>
      </div>

      {/* ================= WEAK POINTS ================= */}
      {sectionTitle('⚠️', 'The weak points that forced the redesign — and their status', C.red)}
      <p style={{ fontSize: 14, lineHeight: 1.7, maxWidth: 860 }}>
        These are the real, verified problems of the legacy system &mdash; each confirmed in the code.
        The status tag shows what Access Control V3 does about each one.
      </p>
      <div style={{ display: 'grid', gap: 12, margin: '16px 0' }}>
        {[
          {
            title: '1. The hall passes are never checked',
            status: 'FIXED IN V3', fixed: true,
            text: 'The legacy Overrides tab saves exceptions that no page or button ever checks. V3 replaces them with Exceptions that the resolver enforces on every check — deny beats grant, expiry respected, every use visible in the Explainer.',
          },
          {
            title: '2. Two dictionaries for the same words',
            status: 'FIXED IN V3', fixed: true,
            text: 'Legacy job titles carry one permission list and ranks carry another, and screen and server read different ones. V3 has exactly one vocabulary — module × level (+ extras) — and one resolver both sides use.',
          },
          {
            title: '3. Rules baked into the program',
            status: 'V3 IS DB-DRIVEN — LEGACY LISTS REMAIN UNTIL CUTOVER', fixed: false,
            text: 'Department maps, “who counts as a foreman” lists, protected emails and one-off menu exceptions live in code today. Everything in V3 (modules, templates, hats, exceptions) is database rows editable right here. The old hardcoded lists disappear as each module cuts over.',
          },
          {
            title: '4. The same logic written twice',
            status: 'ADDRESSED IN V3', fixed: true,
            text: 'Legacy has one copy of the rules in the browser and another on the server, drifting apart. In V3 the server resolver computes the final answer and the browser consumes it verbatim — including which menus to show.',
          },
          {
            title: '5. A safety switch that was never flipped',
            status: 'OBSOLETE IN V3', fixed: true,
            text: 'The legacy machinery has a rollout switch still sitting on “log only, do not block”. V3 has no such mode: its checks either pass or return a clear 403 with the reason.',
          },
          {
            title: '6. One badge for all projects',
            status: 'FIXED IN V3', fixed: true,
            text: 'Legacy gives a person one global role. V3 assignments give everyone a company default template plus a different template per project — see the Assignments tab (“hats”).',
          },
        ].map((w) => (
          <div key={w.title} style={{
            border: `1px solid ${w.fixed ? '#badbcc' : '#f5c6cb'}`,
            borderLeft: `5px solid ${w.fixed ? '#2ecc71' : C.red}`,
            borderRadius: 10, backgroundColor: '#fff', padding: '14px 18px',
          }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 4 }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: w.fixed ? '#1e7e34' : '#a71d2a' }}>{w.title}</span>
              <span style={{
                fontSize: 10.5, fontWeight: 800, letterSpacing: 0.5, padding: '2px 8px', borderRadius: 8,
                backgroundColor: w.fixed ? '#2ecc7118' : '#e74c3c15',
                color: w.fixed ? '#1e7e34' : '#a71d2a',
                border: `1px solid ${w.fixed ? '#2ecc7150' : '#e74c3c40'}`,
              }}>{w.status}</span>
            </div>
            <div style={{ fontSize: 13.5, color: '#555', lineHeight: 1.65 }}>{w.text}</div>
          </div>
        ))}
      </div>

      {/* ================= INDUSTRY PATTERN ================= */}
      {sectionTitle('🏗️', 'Why it is designed this way')}
      <p style={{ fontSize: 14, lineHeight: 1.7, maxWidth: 860 }}>
        This is not an invented design. Large construction-management platforms have converged on the
        same handful of principles over many years, and our system follows them:
      </p>
      <div style={{
        border: `2px solid ${C.green}`, borderRadius: 12, padding: '18px 22px',
        backgroundColor: '#f2fbf5', margin: '10px 0', maxWidth: 900,
      }}>
        <div style={{ fontWeight: 700, fontSize: 14.5, color: '#1e7e34', marginBottom: 8 }}>
          The five principles
        </div>
        <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13.5, lineHeight: 1.8, color: '#333' }}>
          <li>A <strong>small fixed ladder of access levels</strong> per module &mdash; None / View / Standard / Admin. Everyone understands four words.</li>
          <li>Levels are bundled into <strong>named templates</strong> (&ldquo;Project Manager&rdquo;, &ldquo;Estimator&rdquo;) so you assign one thing, not fifty checkboxes.</li>
          <li>Templates are assigned <strong>per person, per project</strong> &mdash; the matrix-organization answer.</li>
          <li><strong>Exceptions are a thin layer on top</strong>, not a parallel system &mdash; and a denial always beats a grant.</li>
          <li>Everything is <strong>edited on the admin screens and stored in the database</strong> &mdash; no rules hidden in program code, no software release needed to change someone&rsquo;s access.</li>
        </ol>
      </div>

      {/* ================= MANY HATS ================= */}
      {sectionTitle('🎩', 'One person, many hats (the matrix-organization question)')}
      <p style={{ fontSize: 14, lineHeight: 1.7, maxWidth: 860 }}>
        In real life the same person is a Technical Coordinator on job A, an Estimator on job B and a
        Project Manager on job C &mdash; with different powers on each. Can today&rsquo;s system do that?
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 14, margin: '16px 0' }}>
        <Card color={C.red} title="The legacy limitation" subtitle="One badge everywhere">
          The legacy system gives a person one global job title, plus optional secondary titles
          (also global, not per project). A per-project override column existed in the database
          but nothing ever filled or read it.
        </Card>
        <Card color={C.green} title="Solved in V3 — the Assignments tab" subtitle="A different hat per project">
          Everyone carries a company default template, and any number of <strong>project hats</strong>:
          TC on job A, Estimator on job B, PM on job C. Inside a project the hat wins; everywhere else
          the default applies. Hats can expire automatically (e.g. a secondment that ends in March).
        </Card>
      </div>

      {/* ================= EXTERNAL USERS ================= */}
      {sectionTitle('🚧', 'People from outside LGL (external users)', '#b45309')}
      <p style={{ fontSize: 14, lineHeight: 1.7, maxWidth: 860 }}>
        A subcontractor&rsquo;s foreman, the client&rsquo;s engineer, a joint-venture partner, a vendor:
        anyone whose e-mail is not an LGL address is an <strong>external user</strong>. They are visitors
        in the building. A visitor never gets an employee badge; they get a visitor pass for one job site.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14, margin: '16px 0' }}>
        <Card color="#b45309" title="Who they are" subtitle="The company kind, not the access">
          Their company is recorded once (subcontractor, engineer, client, joint venture, vendor).
          That is a label for reports and filters. It grants nothing by itself.
        </Card>
        <Card color="#b45309" title="What they may do" subtitle="One External template, one project">
          Access is one of the <strong>External</strong> templates &mdash; Field, Scheduler, Engineer, Client,
          Joint Venture &mdash; given as a <strong>project hat</strong> on one job. No company default,
          no LGL job title (Viewer, Staff, PM&hellip;). The admin screens only offer those choices for an
          outside e-mail, and the server refuses anything else even if someone tried.
        </Card>
        <Card color="#b45309" title="What the wall does" subtitle="Checked on every request">
          After sign-in, an external account is refused on every internal system &mdash; employees, users,
          estimating, forecasting, procurement, time, dollars, resource plans &mdash; and on any project they
          are not assigned to (that project simply does not exist for them). Inside their own job the
          department and trade questions are skipped: the project hat is the whole answer.
        </Card>
      </div>
      <div style={{
        backgroundColor: C.amberBg, border: `1px solid ${C.amberBorder}`, borderRadius: 10,
        padding: '12px 16px', fontSize: 13.5, lineHeight: 1.65, maxWidth: 900,
      }}>
        <strong>To invite one:</strong> Users tab &rarr; provision the e-mail with the role <strong>External</strong>
        (the only role an outside e-mail can carry). Then Assignments tab &rarr; add a hat on their job with an
        External template, with an end date if the engagement has one. Sign-in is by invitation: an outside
        e-mail that nobody provisioned is turned away at the door and no account is created.
      </div>

      {/* ================= GLOSSARY ================= */}
      {sectionTitle('📖', 'Cheat-sheet: our words vs. plain words')}
      <div style={{ overflowX: 'auto', margin: '14px 0', maxWidth: 720 }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
          <thead>
            <tr style={{ backgroundColor: C.ink, color: '#fff' }}>
              <th style={{ padding: '9px 14px', textAlign: 'left' }}>When we say&hellip;</th>
              <th style={{ padding: '9px 14px', textAlign: 'left' }}>We mean&hellip;</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Role / Role template', 'Job title — decides menus, department, project reach'],
              ['Authority level', 'Rank — decides actions: view, edit, import, delete, configure'],
              ['Department', 'Building floor — Construction, Accounting, Estimating…'],
              ['Sub-department / Trade', 'Room on the floor — Piping, Mechanical, Electrical…'],
              ['Scope', 'Project reach — every project, or only assigned ones'],
              ['Override', 'Hall pass — a one-person exception, maybe temporary'],
              ['Superadmin', 'Master key — skips every check'],
              ['External user', 'Visitor — a non-LGL e-mail; one External template on one project, nothing company-wide'],
              ['Organization', 'The visitor’s company and its kind — subcontractor, engineer, client, joint venture, vendor'],
              ['Frontend check', 'Politeness — hides menus so screens stay clean'],
              ['Backend check', 'Security — the server refusing, no matter what the screen shows'],
            ].map(([term, plain], idx) => (
              <tr key={term} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                <td style={{ padding: '8px 14px', borderBottom: '1px solid #f0f0f0', fontWeight: 600 }}>{term}</td>
                <td style={{ padding: '8px 14px', borderBottom: '1px solid #f0f0f0' }}>{plain}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PermissionsHelpTab;
